import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [sideMenuOpen, setSideMenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About' },
    { path: '/portfolio', label: 'Portfolio' },
    { path: '/contact', label: 'Contact' },
  ];

  const servicesList = [
    'Industrial Wastewater Treatment',
    'Industrial Engineering Services',
    'IT Solutions for Industries',
    'Water Recycling Solutions',
    'Custom ETP Design',
    'ETP Manufacturing',
  ];

  const productsList = [
    'CA Treat-2003',
    'WPC',
    'DEO-2007',
    'TBT Tech-2005',
    'AEFS BIO',
    'AEFS CHEM',
    'Power FLOC-253'
  ];

  const closeSideMenu = () => {
    setSideMenuOpen(false);
    setServicesOpen(false);
    setProductsOpen(false);
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'glass py-3' : 'py-5'
        }`}
      >
        <div className="max-w-full px-2 sm:px-3 lg:px-4">
          <div className="flex items-center justify-between">
            {/* Left: Hamburger + Logo */}
            <div className="flex items-center space-x-3">
              {/* Side Menu Button */}
              <button
                onClick={() => setSideMenuOpen(!sideMenuOpen)}
                className="glass p-2 rounded-lg text-white hover:bg-white/10 transition-all"
                data-testid="side-menu-btn"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>

              {/* Logo */}
              <Link to="/" className="flex items-center space-x-2">
                <div className="w-10 h-10 rounded-lg neon-border flex items-center justify-center bg-gradient-to-br from-purple-400 to-purple-600">
                  <img
                    src="/logo.png"
                    alt="Logo"
                    className="w-full h-full object-contain"
                  />
                </div>
                <span className="text-white font-bold text-xl hidden sm:block">Research and Solution</span>
              </Link>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-white font-medium transition-all duration-300 hover:text-purple-300 ${
                    location.pathname === link.path ? 'neon-text' : ''
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <Link
                to="/contact"
                className="neon-button px-5 py-1.5 rounded-full text-white text-sm font-semibold"
                data-testid="nav-get-quote-btn"
              >
                Get Quote
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden glass p-2 rounded-lg text-white"
              data-testid="mobile-menu-btn"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {mobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden mt-4 glass rounded-lg p-4 space-y-3 fade-in">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block text-white font-medium transition-all duration-300 hover:text-purple-300 ${
                    location.pathname === link.path ? 'neon-text' : ''
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <Link
                to="/contact"
                onClick={() => setMobileMenuOpen(false)}
                className="block neon-button px-6 py-2 rounded-full text-white font-semibold text-center"
              >
                Get Quote
              </Link>
            </div>
          )}
        </div>
      </nav>

      {/* Overlay */}
      {sideMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-[60] transition-opacity"
          onClick={closeSideMenu}
        />
      )}

      {/* Side Menu Panel */}
      <div
        className={`fixed top-0 left-0 h-full w-72 z-[70] transform transition-transform duration-300 ease-in-out ${
          sideMenuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{ background: 'rgba(26, 26, 46, 0.97)', backdropFilter: 'blur(16px)' }}
      >
        {/* Side Menu Header */}
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <span className="text-white font-bold text-lg">Menu</span>
          <button
            onClick={closeSideMenu}
            className="text-white/70 hover:text-white transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Side Menu Content */}
        <div className="p-4 space-y-1">
          {/* Services Dropdown */}
          <div>
            <button
              onClick={() => setServicesOpen(!servicesOpen)}
              className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-white font-medium hover:bg-white/10 transition-all"
            >
              <span>Services</span>
              <svg
                className={`w-4 h-4 transition-transform duration-200 ${servicesOpen ? 'rotate-180' : ''}`}
                fill="none" stroke="currentColor" viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {servicesOpen && (
              <div className="ml-3 mt-1 space-y-0.5 border-l border-purple-500/30 pl-3">
                {servicesList.map((item, idx) => (
                  <Link
                    key={idx}
                    to="/#services"
                    onClick={closeSideMenu}
                    className="block px-3 py-2 rounded-md text-sm text-white/80 hover:text-white hover:bg-white/5 transition-all"
                  >
                    {item}
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Products Dropdown */}
          <div>
            <button
              onClick={() => setProductsOpen(!productsOpen)}
              className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-white font-medium hover:bg-white/10 transition-all"
            >
              <span>Products</span>
              <svg
                className={`w-4 h-4 transition-transform duration-200 ${productsOpen ? 'rotate-180' : ''}`}
                fill="none" stroke="currentColor" viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {productsOpen && (
              <div className="ml-3 mt-1 space-y-0.5 border-l border-purple-500/30 pl-3">
                {productsList.map((item, idx) => (
                  <Link
                    key={idx}
                    to="/"
                    onClick={closeSideMenu}
                    className="block px-3 py-2 rounded-md text-sm text-white/80 hover:text-white hover:bg-white/5 transition-all"
                  >
                    {item}
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Navigation;
